"use client";

import { GameRow } from "@/components/feature/game-row";
import { type ContentOrderGameRow, organizeContentOrders } from "@/lib/content-orders";
import type { GameRow as GameRowType } from "@/lib/games";
import type { PageOriginContext } from "@/lib/page-origin-context";
import { isMobileDevice } from "@/lib/utils/device-detection";
import type { ContentOrderWithGames } from "@/types/content-order";
import { useMemo } from "react";

interface GamesRowsProps {
  contentOrders?: ContentOrderWithGames[];
  emptyMessage?: string;
  emptyDescription?: string;
  pageOrigin?: PageOriginContext | null;
}

export function GamesRows({
  contentOrders,
  emptyMessage = "No Rows Available",
  emptyDescription = "No games are organized in rows yet.",
  pageOrigin,
}: GamesRowsProps) {
  const isMobile = useMemo(() => isMobileDevice(), []);

  const gameRows = useMemo(() => {
    if (contentOrders && contentOrders.length > 0) {
      const organized = organizeContentOrders(contentOrders);
      return organized
        .map((orderRow: ContentOrderGameRow, index: number) => {
          let rowId: number;
          const orderId = orderRow.order.id;
          if (orderId.startsWith("temp-")) {
            rowId = -(index + 1);
          } else {
            const uuid = orderId.replace(/-/g, "");
            const firstPart = Number.parseInt(uuid.slice(0, 8), 16);
            const lastPart = Number.parseInt(uuid.slice(-8), 16);
            rowId = (firstPart ^ lastPart) + 2000000;
          }

          const rowGames = orderRow.games.filter((g) => (isMobile ? !g.desktop_only : true));

          return {
            tag: {
              id: rowId,
              name: orderRow.order.name,
              slug: orderRow.order.slug,
              description: orderRow.order.description,
              display_order: orderRow.order.display_order,
            },
            games: rowGames,
            _orderId: orderId,
          };
        })
        .filter((row) => row.games.length > 0);
    }

    return [];
  }, [contentOrders, isMobile]);

  if (gameRows.length === 0) {
    return (
      <div className="bg-white/10 p-8 rounded-2xl text-center">
        <h2 className="text-xl font-bold mb-2">{emptyMessage}</h2>
        <p>{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {gameRows.map((row: GameRowType & { _orderId?: string }) => {
        // Use UUID string as key for content orders to avoid collisions, fallback to id for tag-based rows
        const rowKey = row._orderId ? `content-order-${row._orderId}` : row.tag.id;
        const previewRowScope = row._orderId ?? `tag-${row.tag.id}`;
        return (
          <GameRow
            key={rowKey}
            tag={row.tag}
            games={row.games}
            previewRowScope={previewRowScope}
            pageOrigin={pageOrigin}
          />
        );
      })}
    </div>
  );
}
